import { inputBaseClasses, ScopedCssBaseline, ThemeProvider, styled } from '@mui/material';
import { CoauthorConfig, CoauthorDialog as BaseCoauthorDialog, CoauthorProvider, useCoauthor } from '@workboard/ui-okr';
import { ComponentProps, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { GlobalStyle, theme } from './theme/theme';
import { CoauthorEvent } from '@workboard/ui-okr/lib/CoauthorChat/types';

const StyledCssBaseline = styled(ScopedCssBaseline)({
  'body #add_goal_page & [role="dialog"][aria-label="Co-Author"]': { // workaround for conflicts with Direct & Dotted page CSS
    '& button': {
      fontSize: '16px'
    },
    [`& textarea.${inputBaseClasses.input}`]: {
      backgroundColor: 'transparent',
      width: '100%',
      height: 'auto',
      minHeight: 'unset',
      '&, &::placeholder': {
        fontSize: '16px'
      }
    },
  },
});

function CoauthorDialog() {
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  const coauthor = useCoauthor();
  useEffect(() => {
    window.openOkrCoauthor = (e: CoauthorEvent, portalNode: HTMLElement) => {
      coauthor.send(e);
      setPortalNode(portalNode);
    }
  }, [coauthor]);

  if (!portalNode) return null;
  return createPortal(
    <StyledCssBaseline>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <BaseCoauthorDialog />
      </ThemeProvider>
    </StyledCssBaseline>,
    portalNode
  );
}

const baseUrl = window.location.origin + '/wb/vnext/gen-ai';

function CoauthorDialogWithProvider() {
  const handleConfigChange = async (config: CoauthorConfig) => {
    const event = new CustomEvent('coauthorConfigChange', { detail: config });
    window.dispatchEvent(event);
  };
  const handleSaveObjective = async (objective: string, originatorId: string) => {
    const event = new CustomEvent('coauthorSaveObjective', { detail: { value: objective, originatorId }});
    window.dispatchEvent(event);
  };
  const handleSaveKeyResult = async (title: string, krSlotIndex: number, originatorId: string) => {
    const event = new CustomEvent('coauthorSaveKeyResult', { detail: { value: title, originatorId, krSlotIndex }});
    window.dispatchEvent(event);
  };

  const handleAnalyticsEvent: ComponentProps<typeof CoauthorProvider>['onTrackAnalyticsEvent'] = (e) => {
    const { name, ...properties } = e;
    window.wb_track(name, {...properties, location: 'OKR_WIZARD' });
  };

  const coauthorLanguage =
    typeof window.wbConfig.coauthorLanguage === 'string'
      ? window.wbConfig.coauthorLanguage
      : 'en';

  return (
    <CoauthorProvider
      baseUrl={baseUrl}
      onConfigChange={handleConfigChange}
      onSaveObjective={handleSaveObjective}
      onSaveKeyResult={handleSaveKeyResult}
      onTrackAnalyticsEvent={handleAnalyticsEvent}
      language={coauthorLanguage}
      location="OKR_WIZARD"
    >
      <CoauthorDialog />
    </CoauthorProvider>
  );
}

export { CoauthorDialogWithProvider as CoauthorDialog };
